// @givmo/fundraisers-module — the fundraiser page wizard (EMC-WEB6): a signed-in person creates their own
// fundraiser page under a campaign, on the web, in place, from a Board Ask's "Create your page" (the
// director's Board page) or a campaign page's "Create your own fundraiser page" (the donate site). Built
// to Jonathan's approved walkthrough frames 27–30 and his rulings of 2026-10-31 22:05–23:04: three steps,
// Goal, "Tell your story" and "Your cover photo"; no title step (her name comes from her account); "Save
// and continue" and "Save and exit" on every step; Finish puts the page live; then "Your page is live",
// with the page's preview, her link and Copy, "See your page" and "Back to the ask". "Edit your page"
// reopens the same three steps.
//
// It stays an ordinary Givmo fundraiser (a P2P supporter page, the same in the apps), through the calls
// the apps make:
//   - it opens on the page she has, the host's (`existing`) or else her own standing's
//     (`GET /campaigns/{id}/join-requests/mine`), so someone who comes back meets her own goal and story,
//     and a page's goal always wins over the campaign's suggestion;
//   - without a page, the goal's act joins the campaign HELD (`POST /campaigns/{id}/join?held=true`,
//     EMC-WEB6's backend half): the page is made as a Draft, nothing public, and starts with the
//     campaign's cover;
//   - the story and the goal after that are the owner's update (`PUT /fundraisers/{id}`), the cover the
//     owner's upload (`POST /fundraisers/{id}/profile_picture`);
//   - Finish publishes a Draft (`POST /fundraisers/{id}/publish`); a live page's Finish only saves.
// The query parameter is a route-level hatch ahead of the snapshot's regen (agent_docs/api-client.md, "A
// whole ROUTE the snapshot lacks", its query-parameter paragraph). A backend with the release off ignores
// it and publishes at once, so a host mounts the wizard only where the release is on (the donate route
// redirects to its join page otherwise).
//
// The link on "Your page is live" is the host's (`shareLink`): on a Board Ask, the one link the ask gave
// her, which the share kit points at her page once it is live, never a second link.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  formatMoneyDisplay,
  givmoClient,
  isGivmoError,
  sdk,
  type Client,
  type FundraiserOut,
} from '@givmo/api-client';
import { Icon, cn, testIdProp } from '@givmo/design-system';
import { Check, Copy, Link2 } from 'lucide-react';
import {
  GOAL_MESSAGE,
  STORY_MAX,
  canonicalGoal,
  cleanGoal,
  displayGoal,
  isGoalish,
} from './fundraiser-form';
import {
  COVER_ACCEPT,
  COVER_MAX_BYTES,
  COVER_TOO_LARGE_MESSAGE,
  COVER_WRONG_TYPE_MESSAGE,
  isAcceptableCover,
} from './cover';
import {
  CoverFrame,
  NEXT_ARROW,
  StageButton,
  StageField,
  StageTextArea,
  WizardActions,
  WizardQuestion,
  WizardStage,
} from './creation-wizard-stage';
import {
  PAGE_WIZARD_COPY as C,
  campaignTitle,
  campaignWord,
  initialsOf,
} from './fundraiser-page-wizard-copy';

/** Her page under the campaign: the supporter overlay and the fundraiser it names. */
export interface FundraiserPageRef {
  supporterId: string;
  fundraiserId: string;
}

export interface FundraiserPageWizardProps {
  /** The campaign the page raises for. */
  campaignId: string;
  /** Her page, when the host knows she has one ("Edit your page"). Absent, the goal's act finds or makes it. */
  existing?: FundraiserPageRef | null;
  /** The signed-in client; the shared one by default. */
  client?: Client;
  /** Her link on "Your page is live". */
  shareLink: (page: FundraiserPageRef) => Promise<string>;
  /** Where "See your page" opens her page. */
  pageUrl: (page: FundraiserPageRef) => string;
  /** "Back to the ask", where the host has one to go back to. */
  back?: { label: string; onBack: () => void };
  /** "Save and exit" and "Close": the wizard has saved what she answered. */
  onExit: () => void;
  /** Her page was made or changed, so the host can read it again. */
  onPageChanged?: (page: FundraiserPageRef) => void;
}

type StepId = 'goal' | 'story' | 'cover' | 'live';
const STEPS: readonly StepId[] = ['goal', 'story', 'cover'];

interface Campaign {
  name: string;
  title: string;
  word: string;
  organization: string | null;
  goal: string | null;
  cover: string | null;
  story: string | null;
}

interface Person {
  name: string;
  logo: string | null;
}

interface Page {
  ref: FundraiserPageRef;
  live: boolean;
  goal: string;
  story: string;
  cover: string | null;
}

/** Her page under the campaign, from her own standing; null with none, or where it isn't served. */
async function myPage(client: Client, campaignId: string): Promise<FundraiserPageRef | null> {
  const { data } = await sdk.p2pGetMyCampaignJoinRequest({
    client,
    path: { campaign_id: campaignId },
  });
  // `supporter_fundraiser_id` is ahead of the snapshot: read by hand.
  const standing = data as
    | { supporter_id?: unknown; supporter_fundraiser_id?: unknown }
    | undefined;
  return typeof standing?.supporter_id === 'string' &&
    typeof standing.supporter_fundraiser_id === 'string'
    ? { supporterId: standing.supporter_id, fundraiserId: standing.supporter_fundraiser_id }
    : null;
}

function refusalWords(caught: unknown, fallback: string): string {
  return isGivmoError(caught) && caught.message ? caught.message : fallback;
}

/** A money value the reads serve as a number or a string, as the decimal string the form keeps. */
function moneyString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function readCampaign(row: FundraiserOut): Campaign {
  const organization =
    row.charity_owner?.name ?? row.owner_company?.name ?? row.owner_group?.name ?? null;
  const goal = moneyString(row.goal_amount);
  return {
    name: row.name,
    title: campaignTitle(row.name, organization),
    word: campaignWord(row.name),
    organization,
    goal: goal ? `$${formatMoneyDisplay(goal, { cents: 'when-present' }) ?? goal}` : null,
    cover: row.image_url ?? null,
    story: row.description ?? row.story ?? null,
  };
}

function readPage(row: FundraiserOut, ref: FundraiserPageRef, campaign: Campaign | null): Page {
  const story = row.description ?? row.story ?? '';
  return {
    ref,
    live: row.status === 'Active',
    goal: displayGoal(moneyString(row.goal_amount)),
    // A new page carries its campaign's story until she writes her own: the field starts empty then.
    story: campaign?.story && story === campaign.story ? '' : story,
    cover: row.image_url ?? null,
  };
}

export function FundraiserPageWizard({
  campaignId,
  existing = null,
  client = givmoClient,
  shareLink,
  pageUrl,
  back,
  onExit,
  onPageChanged,
}: FundraiserPageWizardProps) {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [page, setPage] = useState<Page | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState<StepId>('goal');
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [goal, setGoal] = useState('');
  const [story, setStory] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  /** She replaced the cover here, so it's no longer the campaign's photo. */
  const [replacedCover, setReplacedCover] = useState(false);
  /** The campaign's "Suggested goal for fundraiser pages", the Goal step's start on a new page. */
  const [suggestedGoal, setSuggestedGoal] = useState<string | null>(null);
  /** What the Goal field opened with, so leaving knows whether she answered it. */
  const [startGoal, setStartGoal] = useState('');
  const titleId = useId();
  const lineId = useId();
  const firstInput = useRef<HTMLElement | null>(null);

  // What the steps need: the campaign, who she is, and her page when the host knows it.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [campaignRead, meRead, settingsRead] = await Promise.all([
          sdk.fundraisersGetFundraiser({ client, path: { fundraiser_id: campaignId } }),
          sdk.usersGetMe({ client }),
          sdk.p2pGetCampaignP2P({ client, path: { campaign_id: campaignId } }),
        ]);
        if (campaignRead.error || !campaignRead.data) throw campaignRead.error;
        const read = readCampaign(campaignRead.data);
        const me = meRead.data;
        const name = me ? [me.first_name, me.last_name].filter(Boolean).join(' ').trim() : '';
        const known = existing ?? (await myPage(client, campaignId));
        let mine: Page | null = null;
        if (known) {
          const own = await sdk.fundraisersGetOwnFundraiser({
            client,
            path: { fundraiser_id: known.fundraiserId },
          });
          if (own.error || !own.data) throw own.error;
          mine = readPage(own.data as FundraiserOut, known, read);
        }
        if (cancelled) return;
        // Served while the email campaigns release is on (ahead of the snapshot): read by hand.
        const suggested = (settingsRead.data as { suggested_page_goal?: unknown } | undefined)
          ?.suggested_page_goal;
        const suggestion = typeof suggested === 'string' && suggested ? suggested : null;
        setSuggestedGoal(suggestion);
        setCampaign(read);
        setPerson({ name, logo: me?.logo_url ?? null });
        setPage(mine);
        const opened = mine ? mine.goal : displayGoal(suggestion);
        setGoal(opened);
        setStartGoal(opened);
        setStory(mine ? mine.story : '');
      } catch (caught) {
        if (!cancelled) setLoadError(refusalWords(caught, C.loadFailed));
      }
    })();
    return () => {
      cancelled = true;
    };
    // The wizard reads once, for the campaign and the page it opened on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, existing?.fundraiserId]);

  useEffect(() => {
    firstInput.current?.focus({ preventScroll: true });
  }, [step, campaign]);

  // ── The writes ─────────────────────────────────────────────────────────────────────────────────

  async function ownRead(ref: FundraiserPageRef): Promise<Page> {
    const own = await sdk.fundraisersGetOwnFundraiser({
      client,
      path: { fundraiser_id: ref.fundraiserId },
    });
    if (own.error || !own.data) throw own.error;
    return readPage(own.data as FundraiserOut, ref, campaign);
  }

  async function update(ref: FundraiserPageRef, body: Record<string, unknown>) {
    const { error: refused } = await sdk.fundraisersUpdateFundraiser({
      client,
      path: { fundraiser_id: ref.fundraiserId },
      body,
    });
    if (refused) throw refused;
  }

  /** The goal she has, as the page holds it: the join makes the page, or the update writes it. */
  async function saveGoal(value: string): Promise<Page> {
    const cleaned = cleanGoal(value);
    if (page === null) {
      const { data, error: refused } = await client.post({
        url: `/campaigns/${encodeURIComponent(campaignId)}/join`,
        query: { held: true },
        body: cleaned ? { goal_amount: cleaned } : {},
        headers: { 'Content-Type': 'application/json' },
      });
      if (refused) throw refused;
      const result = data as {
        created?: boolean;
        supporter?: { id?: string; supporter_fundraiser_id?: string };
      };
      const supporterId = result.supporter?.id;
      const fundraiserId = result.supporter?.supporter_fundraiser_id;
      if (!supporterId || !fundraiserId) throw new Error(C.saveFailed);
      const ref = { supporterId, fundraiserId };
      let made = await ownRead(ref);
      // A page that appeared since the wizard opened: the join answered it as it is. Its goal wins
      // over the suggestion she left alone; a goal she typed is written to it.
      if (
        result.created === false &&
        canonicalGoal(cleaned) !== canonicalGoal(cleanGoal(startGoal)) &&
        canonicalGoal(cleaned) !== canonicalGoal(made.goal)
      ) {
        await update(ref, cleaned ? { goal_amount: cleaned } : { remove_goal: true });
        made = await ownRead(ref);
      }
      onPageChanged?.(ref);
      return made;
    }
    if (canonicalGoal(cleaned) !== canonicalGoal(page.goal)) {
      await update(page.ref, cleaned ? { goal_amount: cleaned } : { remove_goal: true });
      onPageChanged?.(page.ref);
      return { ...page, goal: cleaned ? displayGoal(cleaned) : '' };
    }
    return page;
  }

  async function saveStory(current: Page, value: string): Promise<Page> {
    const trimmed = value.trim();
    if (trimmed === current.story.trim()) return current;
    // Emptied: the page goes back to what a new page says, its campaign's story.
    await update(current.ref, { description: trimmed || (campaign?.story ?? '') });
    onPageChanged?.(current.ref);
    return { ...current, story: trimmed };
  }

  /** Saves the step on screen; answers the page as it then is. */
  async function saveStep(at: StepId, goalValue = goal): Promise<Page | null> {
    if (at === 'goal') {
      if (!isGoalish(goalValue)) throw new GoalRefused();
      return saveGoal(goalValue);
    }
    if (at === 'story' && page) return saveStory(page, story);
    return page;
  }

  async function run(act: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await act();
    } catch (caught) {
      setError(caught instanceof GoalRefused ? GOAL_MESSAGE : refusalWords(caught, C.saveFailed));
    } finally {
      setBusy(false);
    }
  }

  function go(to: StepId, way: 'forward' | 'back') {
    setDirection(way);
    setError(null);
    setStep(to);
  }

  function next(goalValue?: string) {
    void run(async () => {
      const saved = await saveStep(step, goalValue);
      // The join may answer a page she already had: its story is hers to see and change.
      if (saved && page === null) {
        setStory(saved.story);
        if (goalValue === undefined) setGoal(saved.goal);
      }
      if (saved) setPage(saved);
      if (goalValue !== undefined) setGoal(goalValue);
      const at = STEPS.indexOf(step);
      go(STEPS[at + 1] ?? 'live', 'forward');
    });
  }

  function finish() {
    if (!page) return;
    void run(async () => {
      let current = page;
      if (!current.live) {
        const { error: refused } = await sdk.fundraisersPublishFundraiser({
          client,
          path: { fundraiser_id: current.ref.fundraiserId },
        });
        if (refused) throw refused;
        current = { ...current, live: true };
        onPageChanged?.(current.ref);
      }
      setPage(current);
      setLink(null);
      shareLink(current.ref)
        .then(setLink)
        .catch(() => setLink(pageUrl(current.ref)));
      go('live', 'forward');
    });
  }

  /**
   * "Save and exit" keeps what she has so far (frame 28): her goal, and a story she typed and went Back
   * from. A goal she hasn't touched is no answer, so leaving then makes no page.
   */
  function saveAndExit() {
    void run(async () => {
      if (!isGoalish(goal)) throw new GoalRefused();
      const answered = canonicalGoal(cleanGoal(goal)) !== canonicalGoal(cleanGoal(startGoal));
      if (page !== null || answered) await saveStory(await saveGoal(goal), story);
      onExit();
    });
  }

  function pickCover(file: File) {
    if (!page) return;
    if (!isAcceptableCover(file)) return setError(COVER_WRONG_TYPE_MESSAGE);
    if (file.size > COVER_MAX_BYTES) return setError(COVER_TOO_LARGE_MESSAGE);
    setError(null);
    setUploading(true);
    void (async () => {
      try {
        const { data, error: refused } = await sdk.fundraisersUploadFundraiserPicture({
          client,
          path: { fundraiser_id: page.ref.fundraiserId },
          body: { file },
        });
        if (refused) throw refused;
        const cover = (data as FundraiserOut | undefined)?.image_url ?? null;
        // A cache-busting mark, so the replaced photo shows at the same address.
        setPage((current) =>
          current
            ? { ...current, cover: cover ? `${cover}?v=${Date.now()}` : current.cover }
            : current,
        );
        setReplacedCover(true);
        onPageChanged?.(page.ref);
      } catch (caught) {
        setError(refusalWords(caught, C.uploadFailed));
      } finally {
        setUploading(false);
      }
    })();
  }

  // ── The screens ────────────────────────────────────────────────────────────────────────────────

  const stepNumber = step === 'live' ? STEPS.length : STEPS.indexOf(step) + 1;
  const ref = (element: HTMLElement | null) => {
    firstInput.current = element;
  };

  let body: ReactNode;
  if (loadError !== null) {
    body = (
      <p
        role="alert"
        className="text-s19 font-w600 text-on-brand"
        data-testid="page-wizard-load-error"
      >
        {loadError}
      </p>
    );
  } else if (campaign === null) {
    body = null;
  } else if (step === 'live' && page !== null) {
    body = (
      <LiveScreen
        campaign={campaign}
        person={person}
        page={page}
        link={link}
        pageUrl={pageUrl(page.ref)}
        back={back}
        titleRef={ref}
      />
    );
  } else {
    const backButton =
      step === 'goal' ? undefined : () => go(STEPS[STEPS.indexOf(step) - 1] ?? 'goal', 'back');
    const question = (title: string, line?: string) => (
      <WizardQuestion
        stepLabel={C.stepOf(stepNumber, STEPS.length)}
        title={title}
        titleId={titleId}
        line={line}
        lineId={lineId}
        stepTestId="page-wizard-step"
      />
    );
    const act = (label: string, onClick: () => void, arrow = true) => (
      <StageButton
        type="submit"
        busy={busy}
        disabled={uploading}
        trailing={arrow ? NEXT_ARROW : undefined}
        onClick={onClick}
        testId="page-wizard-next"
      >
        {label}
      </StageButton>
    );
    let content: ReactNode;
    if (step === 'goal') {
      const suggested = suggestedGoal !== null && page === null;
      content = (
        <>
          {question(C.goalTitle, C.goalLine(campaign.word, campaign.goal))}
          <div className="mt-32">
            <StageField
              value={goal}
              onChange={setGoal}
              prefix="$"
              inputMode="decimal"
              labelledBy={titleId}
              describedBy={lineId}
              invalid={error === GOAL_MESSAGE}
              inputRef={ref}
              testId="page-wizard-goal"
            />
          </div>
          <div className="mt-16 flex items-center justify-between gap-16">
            <p className="text-s17 font-w500 text-on-brand">{suggested ? C.suggestion : null}</p>
            <button
              type="button"
              onClick={() => next('')}
              disabled={busy}
              className="rounded-all4 text-s17 font-w600 text-on-brand underline decoration-on-brand/60 underline-offset-[5px] outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-brand disabled:cursor-not-allowed"
              data-testid="page-wizard-no-goal"
            >
              {C.noGoal}
            </button>
          </div>
          <WizardActions error={error}>{act(C.saveAndContinue, () => undefined)}</WizardActions>
        </>
      );
    } else if (step === 'story') {
      content = (
        <>
          {question(C.storyTitle, C.storyLine(campaign.organization))}
          <div className="mt-32">
            <StageTextArea
              value={story}
              onChange={setStory}
              maxLength={STORY_MAX}
              labelledBy={titleId}
              describedBy={lineId}
              inputRef={ref}
              testId="page-wizard-story"
            />
          </div>
          <WizardActions
            onBack={backButton}
            backDisabled={busy}
            backTestId="page-wizard-back"
            error={error}
          >
            {act(C.saveAndContinue, () => undefined)}
          </WizardActions>
        </>
      );
    } else {
      const cover = page?.cover ?? null;
      const inherited = cover !== null && campaign.cover !== null && !replacedCover;
      content = (
        <>
          {question(
            C.coverTitle,
            inherited ? C.coverLineInherited(campaign.word) : C.coverLineEmpty,
          )}
          <div className="mt-24">
            <CoverFrame
              src={cover}
              uploading={uploading}
              accept={COVER_ACCEPT}
              onFile={pickCover}
              labelledBy={titleId}
              describedBy={lineId}
              buttonRef={ref}
            />
          </div>
          <WizardActions
            onBack={backButton}
            backDisabled={busy || uploading}
            backTestId="page-wizard-back"
            error={error}
            note={page?.live ? undefined : C.finishNote}
          >
            {act(C.finish, () => undefined, false)}
          </WizardActions>
        </>
      );
    }
    body = (
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (busy || uploading) return;
          if (step === 'cover') finish();
          else next();
        }}
        data-testid={`page-wizard-step-${step}`}
      >
        <WhoHeader campaign={campaign} person={person} />
        {content}
      </form>
    );
  }

  return (
    <WizardStage
      title={C.title}
      contentTop="pt-[56px] max-md:pt-32"
      step={stepNumber}
      total={STEPS.length}
      exitLabel={step === 'live' ? C.close : C.saveAndExit}
      onExit={step === 'live' ? onExit : saveAndExit}
      busy={busy || uploading}
      initialFocus={firstInput}
    >
      <div key={step} className={`fundraiser-wizard-enter-${direction}`}>
        {body}
      </div>
    </WizardStage>
  );
}

/** A goal she typed that the form's rule refuses: said where she presses, and never written. */
class GoalRefused extends Error {}

// ── Her name above what she's raising for ────────────────────────────────────────────────────────

function Avatar({ person, size }: { person: Person | null; size: 'large' | 'small' }) {
  const name = person?.name ?? '';
  const large = size === 'large';
  return person?.logo ? (
    <img
      src={person.logo}
      alt=""
      className={cn(
        'shrink-0 rounded-all100 object-cover',
        large ? 'size-[56px] ring-4 ring-on-brand/40' : 'size-[24px]',
      )}
    />
  ) : (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-all100 font-w800',
        large
          ? 'size-[56px] bg-surface text-s20 text-brand-700 ring-4 ring-on-brand/40'
          : 'size-[24px] bg-brand-500 text-s10 text-on-brand',
      )}
    >
      {initialsOf(name)}
    </span>
  );
}

function WhoHeader({ campaign, person }: { campaign: Campaign; person: Person | null }) {
  return (
    <div className="mb-32 flex items-center gap-16" data-testid="page-wizard-who">
      <Avatar person={person} size="large" />
      <div className="min-w-0">
        <p className="text-s20 font-w800 leading-tight text-on-brand">{person?.name ?? ''}</p>
        <p className="mt-4 text-s16 font-w500 text-on-brand">{C.raisingFor(campaign.title)}</p>
      </div>
    </div>
  );
}

// ── "Your page is live" ──────────────────────────────────────────────────────────────────────────

function LiveScreen({
  campaign,
  person,
  page,
  link,
  pageUrl,
  back,
  titleRef,
}: {
  campaign: Campaign;
  person: Person | null;
  page: Page;
  link: string | null;
  pageUrl: string;
  back?: { label: string; onBack: () => void };
  titleRef: (element: HTMLElement | null) => void;
}) {
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
  const story = page.story || campaign.story || '';
  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied('yes');
    } catch {
      setCopied('failed');
    }
  }
  return (
    <div className="-mt-8" data-testid="page-wizard-live">
      <h1
        ref={titleRef}
        tabIndex={-1}
        className="text-balance text-s48 font-w900 leading-[1.08] tracking-[-1.2px] text-on-brand outline-none max-sm:text-s34"
      >
        {C.liveTitle}
      </h1>
      <p className="mt-14 text-s18 font-w500 leading-[1.45] text-on-brand">
        {C.liveLine(campaign.word)}
      </p>

      <article
        className="fundraiser-wizard-lift mt-28 overflow-hidden rounded-all20 bg-surface"
        data-testid="page-wizard-preview"
      >
        {page.cover ? (
          <img
            src={page.cover}
            alt=""
            className="aspect-[337/100] w-full object-cover object-[50%_30%]"
          />
        ) : null}
        <div className="px-24 py-20 max-sm:px-20">
          <p className="text-s20 font-w800 leading-snug text-text-strong">{campaign.title}</p>
          <p className="mt-2 flex items-center gap-8 text-s14 text-text-secondary">
            <Avatar person={person} size="small" />
            <span>
              <span className="font-w700 text-text-strong">{person?.name ?? ''}</span>’s {C.pageOf}
            </span>
          </p>
          {story ? (
            <p className="mt-12 line-clamp-2 text-s15 leading-[22px] text-text-secondary">
              {story}
            </p>
          ) : null}
        </div>
      </article>

      <div className="mt-16 flex items-center gap-12 rounded-all15 bg-surface py-8 pr-8 pl-18">
        <Icon icon={Link2} size={20} className="shrink-0 text-text-tertiary" aria-hidden />
        <span
          className="min-w-0 flex-1 truncate text-s17 font-w500 text-text-strong select-all"
          data-testid="page-wizard-link"
        >
          {link ? link.replace(/^https?:\/\//, '') : '…'}
        </span>
        <button
          type="button"
          onClick={() => void copy()}
          disabled={!link}
          className="inline-flex h-[40px] shrink-0 items-center gap-8 rounded-all12 bg-brand-50 px-16 text-s16 font-w700 text-brand-700 outline-none hover:bg-brand-100 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:cursor-not-allowed"
          data-testid="page-wizard-copy"
        >
          <Icon icon={copied === 'yes' ? Check : Copy} size={18} aria-hidden />
          {copied === 'yes' ? C.copied : C.copy}
        </button>
      </div>
      {copied === 'failed' ? (
        <p role="alert" className="mt-12 text-s16 font-w600 text-on-brand">
          {C.copyFailed}
        </p>
      ) : null}
      <span className="sr-only" role="status">
        {copied === 'yes' ? C.copied : ''}
      </span>

      <div className="mt-36 flex flex-wrap items-center justify-end gap-12">
        {back ? (
          <button
            type="button"
            onClick={back.onBack}
            className="inline-flex h-[52px] items-center rounded-all50 border-2 border-on-brand/80 px-28 text-s17 font-w700 text-on-brand outline-none hover:bg-on-brand/10 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-brand"
            {...testIdProp('page-wizard-back-to')}
          >
            {back.label}
          </button>
        ) : null}
        <a
          href={pageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="fundraiser-wizard-lift inline-flex h-[52px] items-center rounded-all50 bg-surface px-28 text-s17 font-w700 text-brand-700 outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-brand"
          data-testid="page-wizard-see-page"
        >
          {C.seePage}
        </a>
      </div>
    </div>
  );
}
